import { createContext, useContext, useEffect, useState } from 'react'
import { Navigate, Outlet, useLocation } from 'react-router'
import ROUTES from '@/ROUTES'

import { Whoami } from '@/MODELS/types'
import { IUser } from '@/MODELS/user.model'

import analytics from '@/services/analytics.service'
import { api, clearAccessToken, hasAccessToken, tryRefreshAccessToken } from '@/services/api.service'

import Loading from '@/components/Loading'

import { IPermissions } from '../../../back/src/services/authentication.service'

export interface IAuthContext {
    user: IUser | null
    fetchUser: () => Promise<IUser | null>
    clearUser: () => void
    checkAuth: () => Promise<void>
    state: 'checking' | 'authenticated' | 'unauthenticated'
}

const initialValue: IAuthContext = {
    user: null,
    fetchUser: async () => null,
    clearUser: () => null,
    checkAuth: async () => {},
    state: 'checking',
}

const AuthContext = createContext<IAuthContext>(initialValue)

// Global request tracker to prevent duplicate API calls
let currentAuthCheck: Promise<any> | null = null

export function AuthProvider({ children }: { children: React.ReactNode }) {
    const [user, setUser] = useState<IUser | null>(null)
    const [state, setState] = useState<'checking' | 'authenticated' | 'unauthenticated'>('checking')

    const fetchUser = async (): Promise<IUser | null> => {
        try {
            // Reuse in-flight request if one exists
            if (!currentAuthCheck) {
                currentAuthCheck = api<Whoami>(ROUTES.auth.whoami)
            }

            const response = await currentAuthCheck
            currentAuthCheck = null

            const userData = response?.data?.user ?? null
            setUser(userData)

            if (userData) {
                analytics.setUser(userData)
                setState('authenticated')
            } else {
                analytics.setUser(null)
            }

            return userData
        } catch (error) {
            setUser(null)
            return null
        } finally {
            currentAuthCheck = null
        }
    }

    const clearUser = () => {
        setUser(null)
        setState('unauthenticated')
        clearAccessToken()
    }

    const checkAuth = async () => {
        // If we already have a user, they're authenticated
        if (user) {
            setState('authenticated')
            return
        }

        // If no access token in memory, try to refresh from cookie
        if (!hasAccessToken()) {
            const refreshed = await tryRefreshAccessToken()
            if (!refreshed) {
                setState('unauthenticated')
                return
            }
        }

        // We have an access token — validate it
        try {
            const authUser = await fetchUser()
            if (authUser) {
                setState('authenticated')
            } else {
                setState('unauthenticated')
            }
        } catch (error) {
            setState('unauthenticated')
        }
    }

    return (
        <AuthContext.Provider value={{ user, fetchUser, clearUser, checkAuth, state }}>{children}</AuthContext.Provider>
    )
}

export const useAuth = () => useContext(AuthContext)

/*  Route Wrappers - use in index.tsx
    SHOULD BE NESTED INSIDE THE AuthContext.Provider
*/

export function AuthenticatedWrapper() {
    const { user, checkAuth, state } = useAuth()

    // Check auth status using the lastAuthCheck cache
    useEffect(() => {
        checkAuth()
    }, [user])

    if (state === 'checking') return <Loading mode='block' />
    if (state === 'unauthenticated') return <Navigate to='/login' replace />
    return <Outlet />
}

export function PermissionsWrapper({ required }: { required: Partial<IPermissions> }) {
    const { user } = useAuth()

    if (!user) {
        return <Outlet />
    }

    if (required?.isAdmin && user?.permissions?.isAdmin) {
        return <Outlet />
    }

    return <Navigate to={'/forbidden'} replace />
}

export function OnboardingWrapper() {
    const { user } = useAuth()
    const location = useLocation()

    // No need to fetch again - rely on AuthProvider
    if (import.meta.env.VITE_ONBOARDING === 'true') {
        if (!user) {
            return <Loading mode='block' />
        }
        // The Let's Go button on the celebration screen passes location.state.fromCelebration
        // so we don't bounce the user back to /onboarding step 1 if the post-submit setUser
        // hasn't flushed by the time this wrapper renders. Without this bypass there's a small
        // race where stale user.onboardingComplete:false is read on the navigate, redirecting
        // momentarily to /onboarding before the new user state catches up.
        const fromCelebration = (location.state as { fromCelebration?: boolean } | null)?.fromCelebration
        if (!user.onboardingComplete && !fromCelebration) {
            return <Navigate to='/onboarding' replace />
        }
    }

    return <Outlet />
}
